import { type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { AlignJustify, BarChart3, DollarSign, Inbox, LogOut, PanelLeft, Settings, Truck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { GreenlightLogomark } from './GreenlightLogo';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTitle } from './ui/sheet';
import { cn } from '../lib/utils';

// ── nav structure ─────────────────────────────────────────────────────────────

interface SubItem {
  to: string;
  label: string;
}

interface Section {
  id: string;
  icon: ReactNode;
  label: string;
  to?: string;
  end?: boolean;
  adminOnly?: boolean;
  children?: SubItem[];
}

const ICON = 'h-4 w-4 shrink-0';

const SECTIONS: Section[] = [
  {
    id: 'dashboard',
    icon: <BarChart3 className={ICON} />,
    label: 'Dashboard',
    to: '/',
    end: true,
  },
  {
    id: 'price-checks',
    icon: <DollarSign className={ICON} />,
    label: 'Price Checks',
    children: [
      { to: '/price-checks/review',    label: 'Review'    },
      { to: '/price-checks/queue',     label: 'Queue'     },
      { to: '/price-checks/processed', label: 'Processed' },
    ],
  },
  {
    // Truck: the section is about how fast a part reaches the customer, which is
    // the one thing every Home Value proposal changes on the storefront.
    id: 'home-value',
    icon: <Truck className={ICON} />,
    label: 'Home Value',
    children: [
      { to: '/home-value/review',    label: 'Review'    },
      { to: '/home-value/queue',     label: 'Queue'     },
      { to: '/home-value/processed', label: 'Processed' },
      // The items the rule could not decide — the client reviews these by hand.
      { to: '/home-value/no-data',   label: 'No data'   },
    ],
  },
  {
    // Inbox, not a "+": the plus glyph reads as "expand" elsewhere in the UI,
    // which reviewers found ambiguous next to the section toggles (BOT-392).
    id: 'new-items',
    icon: <Inbox className={ICON} />,
    label: 'New Items',
    children: [
      { to: '/new-items/review',    label: 'Review'    },
      { to: '/new-items/queue',     label: 'Queue'     },
      { to: '/new-items/processed', label: 'Processed' },
    ],
  },
  {
    id: 'all-items',
    icon: <AlignJustify className={ICON} />,
    label: 'All Items',
    to: '/all-items',
  },
  {
    id: 'admin',
    icon: <Settings className={ICON} />,
    label: 'Admin',
    adminOnly: true,
    children: [
      { to: '/admin/automation', label: 'Automation' },
      { to: '/admin/jobs',       label: 'Job Queue'  },
      { to: '/admin/vendors',    label: 'Vendors'    },
      { to: '/admin/users',      label: 'Users'      },
    ],
  },
];

// ── helpers ───────────────────────────────────────────────────────────────────

function initials(name: string, email: string): string {
  const source = name?.trim() || email.split('@')[0].replace(/[._-]+/g, ' ');
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const ROLE_LABEL: Record<string, string> = {
  admin: 'Administrator',
  reviewer: 'Operations',
  viewer: 'Read only',
};

// ── sidebar content (shared by the desktop column and the mobile sheet) ───────

function SidebarBody({ onToggle, onNavigate }: { onToggle: () => void; onNavigate?: () => void }) {
  const { user } = useAuth();
  const { pathname } = useLocation();

  const visibleSections = SECTIONS.filter(s => !s.adminOnly || user?.role === 'admin');

  async function handleLogout() {
    await fetch('/auth/logout', { credentials: 'include' });
    window.location.href = '/login';
  }

  return (
    <>
      {/* ── header ── */}
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border pl-4 pr-3">
        <GreenlightLogomark size={22} />
        <span className="text-sm font-semibold tracking-[-0.01em]">Greenlight</span>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          title="Toggle sidebar"
          aria-label="Toggle sidebar"
          className="ml-auto"
        >
          <PanelLeft className="h-4 w-4" />
        </Button>
      </div>

      {/* ── nav ── */}
      <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-2 py-3">
        {visibleSections.map(section => {
          if (!section.children) {
            return (
              <NavLink
                key={section.id}
                to={section.to!}
                end={section.end}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'flex h-[34px] items-center gap-2.5 rounded-md px-2.5 font-medium',
                    isActive ? 'bg-secondary text-foreground' : 'text-zinc-700 hover:bg-secondary hover:text-foreground',
                  )
                }
              >
                {section.icon}
                {section.label}
              </NavLink>
            );
          }

          // Sections render permanently expanded — with five nav groups there is
          // nothing to scroll past, so a collapse control would add a click
          // without saving any space (BOT-392).
          const groupActive = section.children.some(c => pathname.startsWith(c.to));

          return (
            <div key={section.id}>
              <div
                className={cn(
                  'mt-1.5 flex h-[34px] items-center gap-2.5 rounded-md px-2.5',
                  groupActive ? 'font-semibold text-foreground' : 'font-medium text-zinc-700',
                )}
              >
                {section.icon}
                {section.label}
              </div>
              <div className="ml-[13px] flex flex-col gap-0.5 border-l border-border pl-[13px]">
                {section.children.map(child => (
                  <NavLink
                    key={child.to}
                    to={child.to}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        'flex h-[30px] items-center rounded-md px-2.5',
                        isActive
                          ? 'bg-secondary font-medium text-foreground'
                          : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                      )
                    }
                  >
                    {child.label}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      {/* ── user ── */}
      <div className="flex shrink-0 items-center gap-2.5 border-t border-border px-3 py-2.5">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-border text-[11px] font-semibold text-zinc-600">
          {user ? initials(user.name, user.email) : '?'}
        </div>
        <div className="min-w-0 flex-1 leading-[1.3]">
          <div className="truncate text-xs font-medium" title={user?.email}>
            {user?.name || user?.email}
          </div>
          <div className="truncate text-[11px] text-zinc-400">
            {ROLE_LABEL[user?.role ?? ''] ?? user?.role}
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={handleLogout} title="Log out" aria-label="Log out"
          className="hover:text-red-500">
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </>
  );
}

// ── sidebar ───────────────────────────────────────────────────────────────────

interface SidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Overlay the content instead of sitting beside it (narrow viewports). */
  overlay: boolean;
}

export default function Sidebar({ open, onOpenChange, overlay }: SidebarProps) {
  if (overlay) {
    // Mobile: a dimmed-backdrop sheet on top of the content, so the main view
    // never gets squeezed to an unreadable width (BOT-392).
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="left" aria-describedby={undefined}>
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarBody onToggle={() => onOpenChange(false)} onNavigate={() => onOpenChange(false)} />
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: the toggle fully hides the column (main content reclaims the
  // space) rather than shrinking it to an icon rail.
  if (!open) return null;

  return (
    <aside className="flex w-[248px] shrink-0 flex-col border-r border-border bg-zinc-50">
      <SidebarBody onToggle={() => onOpenChange(false)} />
    </aside>
  );
}
